import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Loader2,
  Save,
  Trash2,
  Search,
  UserPlus,
  X,
  Calendar as CalendarIcon,
  Pencil,
  TrendingUp,
  Target,
  Building2,
  Percent,
  Clock,
  FileSpreadsheet,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { dealsApi } from "../../lib/copApi";
import { contactsApi } from "../../lib/contactsApi";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuthStore } from "../../store/authStore";
import AddContactDialog from "../../components/contacts/AddContactDialog";
import { nameInitials } from "../../lib/nameInitials";
import { fmtMulti, sumByCurrency } from "../../lib/money";

const CURRENCIES = ["TRY", "USD", "EUR", "GBP"];
const SOURCES = [
  { key: "website", label: "Web sitesi" },
  { key: "referral", label: "Referans" },
  { key: "political", label: "Siyasi Bağlantı" },
  { key: "cold_call", label: "Soğuk Arama" },
  { key: "social_media", label: "Sosyal Medya" },
];
const PRIORITIES = [
  { key: "low", label: "Düşük" },
  { key: "medium", label: "Orta" },
  { key: "high", label: "Yüksek" },
];
// Beklenen kapanış hızlı seçimleri
const DATE_SHORTCUTS = [
  { key: "1w", label: "1 hafta", days: 7 },
  { key: "15d", label: "15 gün", days: 15 },
  { key: "1m", label: "1 ay", days: 30 },
  { key: "3m", label: "3 ay", days: 90 },
  { key: "6m", label: "6 ay", days: 180 },
  { key: "1y", label: "1 yıl", days: 365 },
];

const todayIso = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};
const addDaysIso = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

// 250000 → "250.000". Undefined/empty → "".
const formatThousands = (raw) => {
  if (raw === "" || raw === null || raw === undefined) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (!digits) return "";
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};
const parseThousands = (formatted) =>
  Number(String(formatted).replace(/\./g, "").replace(/,/g, ".")) || 0;

function DealDialog({
  open,
  onOpenChange,
  contacts,
  companies,
  users,
  statuses,
  stages,
  initial,
  defaultStage,
  onSaved,
  onContactCreated,
  inline = false,
}) {
  const isEdit = !!initial?.id;
  const currentUserId = useAuthStore((s) => s.user?.id);
  const firstNonTerminal = stages.find((s) => !s.is_terminal)?.key || stages[0]?.key || "";

  const [form, setForm] = useState({
    title: "",
    company_id: "",
    contact_id: "",
    status_id: "",
    probability: "",
    stage: "",
    source: "",
    owner_user_id: "",
    priority: "medium",
    tags: [],
    value: "",
    currency: "TRY",
    expected_close_date: "",
    notes: "",
    // Smartsheet-aligned fields — surface every column the source sheet
    // carries so a user editing a synced deal never loses fidelity.
    country: "",
    sector: "",
    email: "",
    phone: "",
    participation_type: "",
    participation_cost: "",
    metersquare: "",
    last_contacted: "",
  });
  const [tagDraft, setTagDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [errors, setErrors] = useState({});

  // Whenever the dialog (re)opens, hydrate from `initial` or reset to defaults.
  useEffect(() => {
    if (!open) return;
    const seedStage =
      initial?.stage || defaultStage || firstNonTerminal;
    setForm({
      title: initial?.title || "",
      company_id: initial?.company_id || "",
      contact_id: initial?.contact_id || "",
      status_id: initial?.status_id || "",
      probability:
        initial?.probability !== undefined && initial?.probability !== null
          ? String(initial.probability)
          : "",
      stage: seedStage,
      source: initial?.source || "",
      owner_user_id: initial?.owner_user_id || currentUserId || "",
      priority: initial?.priority || "medium",
      tags: initial?.tags || [],
      value: initial?.value ? formatThousands(initial.value) : "",
      currency: initial?.currency || "TRY",
      expected_close_date: initial?.expected_close_date || "",
      notes: initial?.notes || "",
      country: initial?.country || "",
      sector: initial?.sector || "",
      email: initial?.email || "",
      phone: initial?.phone || "",
      participation_type: initial?.participation_type || "",
      participation_cost:
        initial?.participation_cost !== undefined && initial?.participation_cost !== null
          ? String(initial.participation_cost)
          : "",
      metersquare:
        initial?.metersquare !== undefined && initial?.metersquare !== null
          ? String(initial.metersquare)
          : "",
      last_contacted: initial?.last_contacted || "",
    });
    setTagDraft("");
    setErrors({});
  }, [open, initial, defaultStage, firstNonTerminal, currentUserId]);

  // Filter people by selected company for the Kontak dropdown. When no
  // company is picked the dropdown is disabled entirely.
  const filteredContacts = useMemo(() => {
    if (!form.company_id) return [];
    return contacts.filter((c) => c.company_id === form.company_id);
  }, [contacts, form.company_id]);

  // Contact statuses filtered to kind="company" (matches the Şirket ekle
  // modal's Statü dropdown per spec).
  const companyStatuses = useMemo(
    () => (statuses || []).filter((s) => s.kind === "company" && s.active !== false),
    [statuses],
  );

  const addTag = () => {
    const v = tagDraft.trim();
    if (!v) return;
    if (form.tags.includes(v)) {
      setTagDraft("");
      return;
    }
    setForm((f) => ({ ...f, tags: [...f.tags, v] }));
    setTagDraft("");
  };
  const removeTag = (t) =>
    setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }));

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = "Fırsat başlığı zorunlu";
    if (!form.company_id) e.company_id = "Şirket seç";
    if (!form.contact_id) e.contact_id = "Kontak seç";
    if (!form.status_id) e.status_id = "Statü seç";
    if (!form.stage) e.stage = "Aşama seç";
    if (!form.source) e.source = "Kaynak seç";
    if (form.probability !== "") {
      const p = Number(form.probability);
      if (Number.isNaN(p) || p < 0 || p > 100) e.probability = "0-100 arası";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        company_id: form.company_id,
        contact_id: form.contact_id,
        status_id: form.status_id || null,
        probability:
          form.probability === "" ? null : Number(form.probability),
        stage: form.stage,
        source: form.source,
        owner_user_id: form.owner_user_id || null,
        priority: form.priority || "medium",
        tags: form.tags,
        value: parseThousands(form.value),
        currency: form.currency,
        expected_close_date: form.expected_close_date || null,
        notes: form.notes || "",
        country: form.country.trim() || null,
        sector: form.sector.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        participation_type: form.participation_type.trim() || null,
        participation_cost:
          form.participation_cost === "" ? null : Number(form.participation_cost),
        metersquare: form.metersquare === "" ? null : Number(form.metersquare),
        last_contacted: form.last_contacted || null,
      };
      const doc = isEdit
        ? await dealsApi.update(initial.id, payload)
        : await dealsApi.create(payload);
      toast.success(isEdit ? "Fırsat güncellendi" : "Fırsat oluşturuldu");
      onSaved(doc);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  // When `inline=true` the form renders as a plain block (used inside
  // the detail drawer for edit-in-place); otherwise it wraps in a modal
  // Dialog. Same form state + submit handler either way — no drift.
  const formBody = (
    <div className="space-y-3">
          {/* 1. Fırsat Başlığı */}
          <div>
            <Label>
              Fırsat Başlığı <span className="text-red-500">*</span>
            </Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              autoFocus
              placeholder="Örn. 2026 Q1 CRM sözleşmesi"
              data-testid="deal-title"
            />
            {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title}</p>}
          </div>

          {/* 2. Şirket + Kontak */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label>
                Şirket <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.company_id || "__none__"}
                onValueChange={(v) =>
                  setForm({
                    ...form,
                    company_id: v === "__none__" ? "" : v,
                    // Reset contact when company changes so we don't retain
                    // an orphaned person from a different company.
                    contact_id: "",
                  })
                }
              >
                <SelectTrigger data-testid="deal-company"><SelectValue placeholder="Şirket seç" /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.company_id && <p className="mt-1 text-xs text-red-600">{errors.company_id}</p>}
            </div>
            <div>
              <Label>
                Kontak <span className="text-red-500">*</span>
              </Label>
              <div className="flex items-stretch gap-2">
                <div className="flex-1">
                  <Select
                    value={form.contact_id || "__none__"}
                    disabled={!form.company_id}
                    onValueChange={(v) =>
                      setForm({ ...form, contact_id: v === "__none__" ? "" : v })
                    }
                  >
                    <SelectTrigger data-testid="deal-contact">
                      <SelectValue
                        placeholder={
                          form.company_id ? "Kontak seç" : "Önce şirket seç"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredContacts.length === 0 ? (
                        <SelectItem value="__none__" disabled>
                          — Bu şirkete bağlı kontak yok —
                        </SelectItem>
                      ) : (
                        filteredContacts.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {[c.first_name, c.last_name].filter(Boolean).join(" ") ||
                              c.email}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                {/* + Yeni ekle butonu — bu şirkete kontak yoksa vurgulu görünür. */}
                {form.company_id && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setAddPersonOpen(true)}
                    className={
                      "gap-1 whitespace-nowrap border-zinc-300 text-xs " +
                      (filteredContacts.length === 0
                        ? "border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100"
                        : "")
                    }
                    data-testid="deal-add-contact-btn"
                    title="Bu şirkete kişi ekle"
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    Yeni Kişi
                  </Button>
                )}
              </div>
              {errors.contact_id && <p className="mt-1 text-xs text-red-600">{errors.contact_id}</p>}
            </div>
          </div>

          {/* 3-4. Statü + Olasılık */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label>
                Statü <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.status_id || "__none__"}
                onValueChange={(v) =>
                  setForm({ ...form, status_id: v === "__none__" ? "" : v })
                }
              >
                <SelectTrigger data-testid="deal-status"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                <SelectContent>
                  {companyStatuses.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.status_id && <p className="mt-1 text-xs text-red-600">{errors.status_id}</p>}
            </div>
            <div>
              <Label>Olasılık (%) <span className="text-xs text-zinc-400">opsiyonel</span></Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={form.probability}
                onChange={(e) => setForm({ ...form, probability: e.target.value })}
                placeholder="0-100"
                data-testid="deal-probability"
              />
              {errors.probability && <p className="mt-1 text-xs text-red-600">{errors.probability}</p>}
            </div>
          </div>

          {/* 5-6. Aşama + Kaynak */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label>
                Aşama <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.stage}
                onValueChange={(v) => setForm({ ...form, stage: v })}
              >
                <SelectTrigger data-testid="deal-stage"><SelectValue placeholder="Aşama seç" /></SelectTrigger>
                <SelectContent>
                  {stages.map((s) => (
                    <SelectItem key={s.key} value={s.key}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.stage && <p className="mt-1 text-xs text-red-600">{errors.stage}</p>}
            </div>
            <div>
              <Label>
                Fırsat Kaynağı <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.source || "__none__"}
                onValueChange={(v) => setForm({ ...form, source: v === "__none__" ? "" : v })}
              >
                <SelectTrigger data-testid="deal-source"><SelectValue placeholder="Kaynak seç" /></SelectTrigger>
                <SelectContent>
                  {SOURCES.map((s) => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.source && <p className="mt-1 text-xs text-red-600">{errors.source}</p>}
            </div>
          </div>

          {/* 7-8. Sorumlu + Öncelik */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label>Sorumlu Kişi</Label>
              <Select
                value={form.owner_user_id || "__none__"}
                onValueChange={(v) => setForm({ ...form, owner_user_id: v === "__none__" ? "" : v })}
              >
                <SelectTrigger data-testid="deal-owner"><SelectValue placeholder="Ata" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Atanmamış —</SelectItem>
                  {(users || []).filter((u) => u.active !== false).map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Öncelik</Label>
              <Select
                value={form.priority}
                onValueChange={(v) => setForm({ ...form, priority: v })}
              >
                <SelectTrigger data-testid="deal-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* 9. Etiketler */}
          <div>
            <Label>Etiketler</Label>
            <Input
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault();
                  addTag();
                } else if (e.key === "Backspace" && !tagDraft && form.tags.length > 0) {
                  e.preventDefault();
                  removeTag(form.tags[form.tags.length - 1]);
                }
              }}
              placeholder="Enter ile ekle (örn. vip, gaziantep, çelik)"
              data-testid="deal-tag-input"
            />
            {form.tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="deal-tag-list">
                {form.tags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2.5 py-0.5 text-[11px] font-medium text-violet-800 hover:bg-red-100 hover:text-red-700"
                  >
                    {tag}
                    <X className="h-3 w-3" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 10. Teklif tutarı + Para birimi + Beklenen kapanış */}
          <div className="rounded-lg border border-zinc-200 bg-zinc-50/40 p-3">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
              Teklif ve tarih
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <Label>Teklif Tutarı</Label>
                <Input
                  inputMode="numeric"
                  value={form.value}
                  onChange={(e) =>
                    setForm({ ...form, value: formatThousands(e.target.value) })
                  }
                  onKeyDown={(e) => {
                    const allowed = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End"];
                    if (allowed.includes(e.key)) return;
                    if (e.metaKey || e.ctrlKey) return;
                    if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                  }}
                  placeholder="örn. 250.000"
                  data-testid="deal-value"
                />
              </div>
              <div>
                <Label>Para Birimi</Label>
                <Select
                  value={form.currency}
                  onValueChange={(v) => setForm({ ...form, currency: v })}
                >
                  <SelectTrigger data-testid="deal-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-3">
              <Label>Beklenen Kapanış</Label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <CalendarIcon className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
                  <Input
                    type="date"
                    min={todayIso()}
                    value={form.expected_close_date || ""}
                    onChange={(e) =>
                      setForm({ ...form, expected_close_date: e.target.value })
                    }
                    className="pl-7"
                    data-testid="deal-close-date"
                  />
                </div>
                {form.expected_close_date && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, expected_close_date: "" })}
                    className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-[11px] text-zinc-500 hover:bg-zinc-50"
                    data-testid="deal-close-date-clear"
                  >
                    Temizle
                  </button>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {DATE_SHORTCUTS.map((s) => {
                  const iso = addDaysIso(s.days);
                  const active = form.expected_close_date === iso;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => setForm({ ...form, expected_close_date: iso })}
                      className={
                        "rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition " +
                        (active
                          ? "border-violet-500 bg-violet-100 text-violet-900"
                          : "border-zinc-200 bg-white text-zinc-600 hover:border-violet-300 hover:text-violet-700")
                      }
                      data-testid={`deal-close-shortcut-${s.key}`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Smartsheet-aligned extra fields — mirror the source COP31_SPN_PIPELINE
              sheet 1:1. Editing these here round-trips them via /api/deals; a
              subsequent Smartsheet sync will overwrite the local edits, so this
              is best-of-both: users can tweak, but source of truth stays canonical. */}
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3">
            <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-900">
              <FileSpreadsheet className="h-3.5 w-3.5" />
              Smartsheet alanları
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-[11px]">Ülke</Label>
                <Input
                  data-testid="deal-country"
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  placeholder="Türkiye, Almanya, …"
                />
              </div>
              <div>
                <Label className="text-[11px]">Sektör</Label>
                <Input
                  data-testid="deal-sector"
                  value={form.sector}
                  onChange={(e) => setForm({ ...form, sector: e.target.value })}
                  placeholder="Telekomünikasyon, Finans, …"
                />
              </div>
              <div>
                <Label className="text-[11px]">E-posta</Label>
                <Input
                  data-testid="deal-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="ad@firma.com"
                />
              </div>
              <div>
                <Label className="text-[11px]">Telefon</Label>
                <Input
                  data-testid="deal-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+90 …"
                />
              </div>
              <div>
                <Label className="text-[11px]">Katılım Tipi</Label>
                <Input
                  data-testid="deal-participation-type"
                  value={form.participation_type}
                  onChange={(e) =>
                    setForm({ ...form, participation_type: e.target.value })
                  }
                  placeholder="Climate Summit, Exhibitor…"
                />
              </div>
              <div>
                <Label className="text-[11px]">Katılım Ücreti (USD)</Label>
                <Input
                  data-testid="deal-participation-cost"
                  type="number"
                  value={form.participation_cost}
                  onChange={(e) =>
                    setForm({ ...form, participation_cost: e.target.value })
                  }
                  placeholder="0"
                />
              </div>
              <div>
                <Label className="text-[11px]">Metrekare (m²)</Label>
                <Input
                  data-testid="deal-metersquare"
                  type="number"
                  value={form.metersquare}
                  onChange={(e) => setForm({ ...form, metersquare: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div>
                <Label className="text-[11px]">Son Temas Tarihi</Label>
                <Input
                  data-testid="deal-last-contacted"
                  type="date"
                  value={form.last_contacted}
                  onChange={(e) =>
                    setForm({ ...form, last_contacted: e.target.value })
                  }
                />
              </div>
            </div>
          </div>

          {/* Notlar (opsiyonel, en altta) */}
          <div>
            <Label>Notlar</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              placeholder="İç notlar, sonraki adımlar…"
              data-testid="deal-notes"
            />
          </div>
        </div>
  );

  const footerButtons = (
    <>
      <Button variant="ghost" onClick={() => onOpenChange(false)}>İptal</Button>
      <Button
        onClick={save}
        disabled={busy}
        className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
        data-testid="deal-save-btn"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        Kaydet
      </Button>
    </>
  );

  return (
    <>
    {inline ? (
      <div data-testid="deal-form-inline" className="space-y-3">
        {formBody}
        <div className="flex items-center justify-end gap-2 border-t border-zinc-200 pt-3">
          {footerButtons}
        </div>
      </div>
    ) : (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="deal-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Fırsatı düzenle" : "Yeni fırsat"}</DialogTitle>
        </DialogHeader>

        {formBody}

        <DialogFooter>
          {footerButtons}
        </DialogFooter>
      </DialogContent>
    </Dialog>
    )}

    {/* Nested kişi ekle — Yeni kontak, seçili şirkete önden bağlanmış olarak
        oluşturulur. onCreated bubble ile parent list'e eklenir ve bu form
        `contact_id`'yi yeni kişiye set eder. */}
    <AddContactDialog
      open={addPersonOpen}
      onOpenChange={setAddPersonOpen}
      initialKind="person"
      companies={companies}
      people={contacts}
      defaultCompanyId={form.company_id}
      isNested
      onCreated={(created) => {
        onContactCreated?.(created);
        setForm((f) => ({ ...f, contact_id: created.id }));
      }}
    />
    </>
  );
}

export default function CopDeals() {
  const { user } = useAuthStore();
  const canManage =
    ["super_admin", "ultra_admin"].includes(user?.role) || user?.can_manage_contacts !== false;
  const [stages, setStages] = useState([]);
  const [deals, setDeals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [q, setQ] = useState("");
  // Tag filter — populated from the list of unique tags across all deals.
  // Adding a tag chip via the Smartsheet integration ("Source: pipeline",
  // "Source: prospects", …) makes this a de-facto "source sheet" filter.
  const [tagFilter, setTagFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailDeal, setDetailDeal] = useState(null); // deal viewed in the drawer
  // Set to true when the user clicked the pencil icon on a card — the
  // drawer then opens straight into edit mode instead of read view.
  // Consumed once by the drawer, resets automatically.
  const [startInEditMode, setStartInEditMode] = useState(false);
  const [defaultStage, setDefaultStage] = useState("");
  const [dragId, setDragId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null); // for drop-zone highlight

  // Two horizontally-scrollable strips (summary + kanban) need to stay
  // aligned when the user pans. We bind their scrollLeft in both
  // directions via these refs. Guarded with an "in-progress" flag so
  // one strip's sync doesn't re-trigger the other's handler.
  const summaryScrollRef = useRef(null);
  const kanbanScrollRef = useRef(null);
  const syncingRef = useRef(false);
  const bindScroll = (source, target) => {
    if (!source || !target || syncingRef.current) return;
    syncingRef.current = true;
    target.scrollLeft = source.scrollLeft;
    // Release the guard on next frame — cheaper than a setTimeout.
    requestAnimationFrame(() => {
      syncingRef.current = false;
    });
  };

  const load = async () => {
    try {
      const [sg, dl, ct, co, us, st] = await Promise.all([
        dealsApi.stages(),
        dealsApi.list(),
        contactsApi.list({ kind: "person" }),
        contactsApi.list({ kind: "company" }),
        api.get("/users").then((r) => r.data).catch(() => []),
        contactsApi.listStatuses("company").catch(() => []),
      ]);
      setStages(sg);
      setDeals(dl);
      setContacts(ct);
      setCompanies(co);
      setUsers(us || []);
      // Tag each status with `kind` so the DealDialog filter works uniformly.
      setStatuses((st || []).map((s) => ({ ...s, kind: s.kind || "company" })));
    } catch (err) {
      toast.error(err.message);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const grouped = useMemo(() => {
    const rx = q.trim().toLowerCase();
    const filtered = deals.filter((d) => {
      if (rx && !(d.title || "").toLowerCase().includes(rx)) return false;
      if (tagFilter && tagFilter !== "all") {
        const tags = d.tags || [];
        if (!tags.includes(tagFilter)) return false;
      }
      return true;
    });
    const map = {};
    stages.forEach((s) => (map[s.key] = []));
    filtered.forEach((d) => {
      if (!map[d.stage]) map[d.stage] = [];
      map[d.stage].push(d);
    });
    return map;
  }, [deals, stages, q, tagFilter]);

  // Distinct sorted tag list — small enough to compute on every render.
  const availableTags = useMemo(() => {
    const set = new Set();
    deals.forEach((d) => (d.tags || []).forEach((t) => t && set.add(t)));
    return Array.from(set).sort();
  }, [deals]);

  const totalsByStage = useMemo(() => {
    const t = {};
    Object.entries(grouped).forEach(([k, list]) => {
      t[k] = sumByCurrency(list);
    });
    return t;
  }, [grouped]);

  // --- Pipeline KPIs -------------------------------------------------
  // Everything derived from the current in-memory lists — no extra fetch.
  const kpiStats = useMemo(() => {
    const terminalKeys = new Set(stages.filter((s) => s.is_terminal).map((s) => s.key));
    const wonKey = stages.find((s) => s.is_terminal && (s.key.includes("kazan") || s.key === "won"))?.key;
    const lostKey = stages.find((s) => s.is_terminal && (s.key.includes("kayb") || s.key === "lost"))?.key;
    const openDeals = deals.filter((d) => !terminalKeys.has(d.stage));
    const wonDeals = wonKey ? deals.filter((d) => d.stage === wonKey) : [];
    const lostDeals = lostKey ? deals.filter((d) => d.stage === lostKey) : [];
    const totalValue = openDeals.reduce((s, d) => s + (Number(d.value) || 0), 0);
    // Weighted pipeline value = Σ (value × probability%). Falls back to
    // stage-implied probability when the deal itself doesn't have one, so
    // freshly-created deals still contribute realistically.
    const weightedValue = openDeals.reduce((s, d) => {
      const p = d.probability !== null && d.probability !== undefined ? d.probability : 20;
      return s + (Number(d.value) || 0) * (p / 100);
    }, 0);
    const wonValue = wonDeals.reduce((s, d) => s + (Number(d.value) || 0), 0);
    // Kapanma oranı (Win rate) — of closed deals only.
    const closedCount = wonDeals.length + lostDeals.length;
    const winRate = closedCount > 0 ? Math.round((wonDeals.length / closedCount) * 100) : null;
    // Yaklaşan kapanışlar — 30 gün içinde beklenen kapanışı olan açık deals.
    const now = new Date();
    const in30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const closingSoon = openDeals.filter((d) => {
      if (!d.expected_close_date) return false;
      const cd = new Date(d.expected_close_date);
      return cd >= now && cd <= in30;
    });
    // Gecikmiş — geçmiş tarihli açık deals.
    const overdue = openDeals.filter((d) => {
      if (!d.expected_close_date) return false;
      return new Date(d.expected_close_date) < now;
    });
    return {
      totalDeals: deals.length,
      openCount: openDeals.length,
      totalValue,
      weightedValue,
      totalByCur: sumByCurrency(openDeals),
      weightedByCur: sumByCurrency(openDeals, (d) => {
        const p = d.probability !== null && d.probability !== undefined ? d.probability : 20;
        return (Number(d.value) || 0) * (p / 100);
      }),
      avgByCur: (() => {
        const counts = {};
        openDeals.forEach((d) => {
          const c = d.currency || "TRY";
          counts[c] = (counts[c] || 0) + 1;
        });
        const totals = sumByCurrency(openDeals);
        const m = {};
        for (const c of Object.keys(totals)) m[c] = totals[c] / (counts[c] || 1);
        return m;
      })(),
      wonCount: wonDeals.length,
      wonValue,
      lostCount: lostDeals.length,
      winRate,
      closingSoonCount: closingSoon.length,
      closingSoonValue: closingSoon.reduce((s, d) => s + (Number(d.value) || 0), 0),
      closingSoonByCur: sumByCurrency(closingSoon),
      overdueCount: overdue.length,
      avgValue: openDeals.length ? totalValue / openDeals.length : 0,
    };
  }, [deals, stages]);

  const onDrop = async (stageKey) => {
    setDragOverStage(null);
    if (!dragId) return;
    const cur = deals.find((d) => d.id === dragId);
    if (!cur || cur.stage === stageKey) {
      setDragId(null);
      return;
    }
    // Optimistic — snap the card to the new column before the network confirms.
    setDeals((list) =>
      list.map((d) => (d.id === dragId ? { ...d, stage: stageKey } : d)),
    );
    try {
      const doc = await dealsApi.update(dragId, { stage: stageKey });
      setDeals((list) => list.map((d) => (d.id === doc.id ? doc : d)));
      const stageLabel = stages.find((s) => s.key === stageKey)?.label || stageKey;
      toast.success(`Fırsat "${stageLabel}" aşamasına taşındı`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      // Rollback on failure.
      setDeals((list) =>
        list.map((d) => (d.id === dragId ? { ...d, stage: cur.stage } : d)),
      );
    } finally {
      setDragId(null);
    }
  };

  const remove = async (deal) => {
    if (!window.confirm(`Sil: "${deal.title}"?`)) return;
    try {
      await dealsApi.remove(deal.id);
      setDeals((list) => list.filter((d) => d.id !== deal.id));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const onSaved = (doc) => {
    setDeals((list) => {
      const exists = list.find((d) => d.id === doc.id);
      return exists ? list.map((d) => (d.id === doc.id ? doc : d)) : [doc, ...list];
    });
    setEditing(null);
  };

  const fmtMoney = (v, cur) =>
    new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: cur || "TRY",
      maximumFractionDigits: 0,
    }).format(v || 0);

  // True whenever ANY deal in the payload comes back masked → the
  // caller lacks `can_see_deal_amounts` and every monetary field
  // (per-card AND KPI totals) should render the redacted glyph.
  const amountsMasked = useMemo(
    () => deals.some((d) => d.amount_masked),
    [deals],
  );
  const maskOr = (formatted) => (amountsMasked ? MASKED_AMOUNT : formatted);

  return (
    <div className="space-y-6" data-testid="cop-deals-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">COP 31</div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
            Satış Pipeline
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Fırsatlarını takip et — sürükle bırak ile aşama değiştir.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-56">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Fırsat ara"
              className="pl-7 pr-8"
              data-testid="deals-search-input"
            />
            {q && (
              /* Reset filter — sadece input dolu iken görünür,
                 tıklayınca query'yi temizler ve tüm kanban geri gelir. */
              <button
                type="button"
                onClick={() => setQ("")}
                aria-label="Aramayı temizle"
                title="Aramayı temizle"
                data-testid="deals-search-clear"
                className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {availableTags.length > 0 && (
            <Select value={tagFilter} onValueChange={setTagFilter}>
              <SelectTrigger
                className="h-9 w-40 text-xs"
                data-testid="deals-tag-filter"
              >
                <SelectValue placeholder="Tüm etiketler" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm etiketler</SelectItem>
                {availableTags.map((tag) => (
                  <SelectItem key={tag} value={tag}>
                    {tag}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {canManage && (
            <Button
              onClick={() => {
                setEditing(null);
                setDefaultStage(stages[0]?.key || "");
                setDialogOpen(true);
              }}
              className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="deal-add-btn"
            >
              <Plus className="h-4 w-4" />
              Yeni fırsat
            </Button>
          )}
        </div>
      </div>

      {/* --- Pipeline KPI şeridi --------------------------------------
          Sayfa üstünde açık portföy + ağırlıklı değer + kazanma oranı vs.
          Tümü grouped/deals in-memory üzerinden türetiliyor, ek endpoint yok. */}
      {deals.length > 0 && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6" data-testid="pipeline-dashboard">
          <DealKpi
            tone="violet"
            icon={<Target className="h-4 w-4" />}
            label="Açık Fırsat"
            value={kpiStats.openCount}
            sub={`Toplam ${kpiStats.totalDeals} fırsat`}
            testId="kpi-open-deals"
          />
          <DealKpi
            tone="fuchsia"
            icon={<TrendingUp className="h-4 w-4" />}
            label="Toplam Portföy"
            value={maskOr(fmtMulti(kpiStats.totalByCur))}
            sub={amountsMasked ? "Yetkiniz yok" : `Ort. ${fmtMulti(kpiStats.avgByCur)} / fırsat`}
            testId="kpi-total-value"
          />
          <DealKpi
            tone="sky"
            icon={<Percent className="h-4 w-4" />}
            label="Ağırlıklı Değer"
            value={maskOr(fmtMulti(kpiStats.weightedByCur))}
            sub={amountsMasked ? "Yetkiniz yok" : "Olasılık × değer"}
            testId="kpi-weighted-value"
          />
          <DealKpi
            tone="emerald"
            icon={<Target className="h-4 w-4" />}
            label="Kazanma Oranı"
            value={kpiStats.winRate === null ? "—" : `%${kpiStats.winRate}`}
            sub={
              kpiStats.wonCount + kpiStats.lostCount === 0
                ? "Kapanan fırsat yok"
                : `${kpiStats.wonCount} kazan · ${kpiStats.lostCount} kayıp`
            }
            testId="kpi-win-rate"
          />
          <DealKpi
            tone="amber"
            icon={<Clock className="h-4 w-4" />}
            label="30 Gün İçinde"
            value={kpiStats.closingSoonCount}
            sub={maskOr(fmtMulti(kpiStats.closingSoonByCur))}
            testId="kpi-closing-soon"
          />
          <DealKpi
            tone={kpiStats.overdueCount > 0 ? "red" : "slate"}
            icon={<CalendarIcon className="h-4 w-4" />}
            label="Gecikmiş"
            value={kpiStats.overdueCount}
            sub={
              kpiStats.overdueCount > 0
                ? "Tarihi geçen açık fırsat"
                : "Her şey yolunda"
            }
            testId="kpi-overdue"
          />
        </div>
      )}

      {/* --- Stage özet şeridi ----------------------------------------
          KPI kartlarının altında pipeline'in mini görünümü. Her aşama için
          renkli nokta + adet + toplam değer + progress bar. Bar dolgusu, o
          aşamanın toplam değerinin sayfadaki en büyük değere oranı. */}
      {/* Aşama Özet Çubuğu — her aşama için kompakt bir "başlık kartı":
          renkli nokta + adet + toplam değer + progress bar. Bar dolgusu, o
          aşamanın toplam değerinin sayfadaki en büyük değere oranı.
          Layout, alttaki kanban ile birebir hizalanır: aynı flex + 260px
          genişlik → Kazanıldı özeti tam Kazanıldı sütununun üstünde. */}
      {deals.length > 0 && stages.length > 0 && (
        <div
          ref={summaryScrollRef}
          onScroll={() => bindScroll(summaryScrollRef.current, kanbanScrollRef.current)}
          className="hidden gap-3 overflow-x-auto rounded-xl border border-zinc-200 bg-white p-3 md:flex"
          data-testid="stage-summary-strip"
        >
          {stages.map((s) => {
            const count = grouped[s.key]?.length || 0;
            const totalMap = totalsByStage[s.key] || {};
            const sumMap = (m) =>
              Object.values(m || {}).reduce((a, b) => a + b, 0);
            // Bar genişliği görsel oran içindir — karışık para birimlerinde
            // yaklaşık büyüklük olarak ham toplam kullanılır.
            const total = sumMap(totalMap);
            const maxTotal = Math.max(
              ...Object.values(totalsByStage).map(sumMap),
              1,
            );
            const pct = maxTotal > 0 ? Math.round((total / maxTotal) * 100) : 0;
            return (
              <div
                key={s.key}
                className="w-[260px] shrink-0 cursor-pointer rounded-lg border border-transparent p-2 transition hover:border-zinc-200 hover:bg-zinc-50"
                onClick={() => {
                  // Scroll the corresponding column into view — nice shortcut
                  // on wide tenants with lots of stages.
                  document
                    .querySelector(`[data-testid="deal-column-${s.key}"]`)
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                data-testid={`stage-summary-${s.key}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <span
                      className="inline-block h-2 w-2 flex-none rounded-full"
                      style={{ backgroundColor: s.color }}
                    />
                    <span className="truncate text-[11px] font-medium text-zinc-700">
                      {s.label}
                    </span>
                  </div>
                  {/* Adet — büyük ve belirgin. Kullanıcı isteği: küçük
                      "chip" yerine gözle kolayca ayrılan sayı. */}
                  <span
                    className="text-lg font-bold tabular-nums text-zinc-900"
                    data-testid={`stage-summary-${s.key}-count`}
                  >
                    {count}
                  </span>
                </div>
                <div className="mt-1 text-sm font-semibold text-zinc-900">
                  {maskOr(fmtMulti(totalMap))}
                </div>
                <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-zinc-100">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${pct}%`, backgroundColor: s.color }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Horizontally scrollable kanban — every stage gets a fixed
          min-width so all 7 columns stay side-by-side even on narrow
          screens. Users pan the strip with a native scrollbar. Prevents
          the previous "last stage wraps to a new row" behaviour. */}
      <div
        ref={kanbanScrollRef}
        onScroll={() => bindScroll(kanbanScrollRef.current, summaryScrollRef.current)}
        className="flex flex-col gap-3 pb-2 md:flex-row md:overflow-x-auto"
        data-testid="deals-kanban-scroller"
      >
        {stages.map((s) => {
          const isOver = dragOverStage === s.key && dragId;
          return (
          <div
            key={s.key}
            onDragOver={(e) => {
              e.preventDefault();
              if (dragOverStage !== s.key) setDragOverStage(s.key);
            }}
            onDragLeave={() => setDragOverStage((cur) => (cur === s.key ? null : cur))}
            onDrop={() => onDrop(s.key)}
            className={
              "flex min-h-[240px] w-full shrink-0 flex-col rounded-xl border p-3 transition-all md:w-[260px] " +
              (isOver
                ? "border-2 border-dashed border-violet-500 bg-violet-50/60 ring-2 ring-violet-200"
                : "border-zinc-200 bg-zinc-50/60")
            }
            data-testid={`deal-column-${s.key}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: s.color }}
                />
                <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">
                  {s.label}
                </div>
              </div>
              <div className="text-[10px] text-zinc-400">
                {grouped[s.key]?.length || 0}
              </div>
            </div>
            <div className="mt-0.5 text-[10px] text-zinc-500">
              {maskOr(fmtMulti(totalsByStage[s.key]))}
            </div>
            <div className="mt-3 flex-1 space-y-2 overflow-y-auto">
              {(grouped[s.key] || []).map((d) => {
                const con = contacts.find((c) => c.id === d.contact_id);
                const co = companies.find((c) => c.id === d.company_id);
                const owner = users.find((u) => u.id === d.owner_user_id);
                const priorityStyles = {
                  low: "bg-emerald-50 text-emerald-700",
                  medium: "bg-amber-50 text-amber-700",
                  high: "bg-red-50 text-red-700",
                };
                return (
                  <div
                    key={d.id}
                    draggable={canManage}
                    onDragStart={() => setDragId(d.id)}
                    onDragEnd={() => setDragId(null)}
                    onClick={() => setDetailDeal(d)}
                    className="group cursor-pointer rounded-lg border border-zinc-200 bg-white p-2.5 shadow-sm transition-shadow hover:shadow-md"
                    data-testid={`deal-card-${d.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-zinc-900">
                          {d.title}
                        </div>
                        <div className="mt-1 flex items-center gap-1.5">
                          <span className="text-xs font-semibold text-zinc-700">
                            {isDealMasked(d) ? MASKED_AMOUNT : fmtMoney(d.value, d.currency)}
                          </span>
                          {d.probability !== null && d.probability !== undefined && (
                            <span className="inline-flex items-center gap-0.5 rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">
                              <Percent className="h-2.5 w-2.5" />
                              {d.probability}
                            </span>
                          )}
                          {d.priority && d.priority !== "medium" && (
                            <span
                              className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${priorityStyles[d.priority] || ""}`}
                              title={`Öncelik: ${d.priority}`}
                            >
                              {d.priority === "high" ? "↑" : "↓"}
                            </span>
                          )}
                        </div>
                        {(con || co) && (
                          <div className="mt-1 truncate text-[11px] text-zinc-500">
                            {co?.name}
                            {con && (
                              <>
                                {co ? " · " : ""}
                                {[con.first_name, con.last_name].filter(Boolean).join(" ")}
                              </>
                            )}
                          </div>
                        )}
                        {(owner || d.expected_close_date) && (
                          <div className="mt-1 flex items-center gap-2 text-[10px] text-zinc-400">
                            {owner && (
                              <span className="inline-flex items-center gap-1">
                                <div className="h-4 w-4 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-[8px] font-semibold text-white flex items-center justify-center">
                                  {nameInitials(owner.name)}
                                </div>
                                {owner.name?.split(" ")[0]}
                              </span>
                            )}
                            {d.expected_close_date && (
                              <span className="inline-flex items-center gap-0.5">
                                <Clock className="h-2.5 w-2.5" />
                                {new Date(d.expected_close_date).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" })}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      {canManage && (
                        <div className="flex flex-col gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Open the side drawer directly in edit
                              // mode — a shortcut so users don't have
                              // to click card → Düzenle.
                              setDetailDeal(d);
                              setStartInEditMode(true);
                            }}
                            className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
                            title="Düzenle"
                            data-testid={`deal-edit-${d.id}`}
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              remove(d);
                            }}
                            className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600"
                            title="Sil"
                            data-testid={`deal-delete-${d.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {(grouped[s.key] || []).length === 0 && (
                <div className="rounded-lg border border-dashed border-zinc-200 py-6 text-center text-[11px] text-zinc-400">
                  Boş
                </div>
              )}
            </div>
            {canManage && (
              <button
                type="button"
                onClick={() => {
                  setEditing(null);
                  setDefaultStage(s.key);
                  setDialogOpen(true);
                }}
                className="mt-2 flex w-full items-center justify-center gap-1 rounded-md border border-dashed border-zinc-300 py-1 text-[11px] text-zinc-500 transition-colors hover:bg-white hover:text-zinc-800"
              >
                <Plus className="h-3 w-3" />
                Ekle
              </button>
            )}
          </div>
          );
        })}
      </div>

      <DealDialog
        open={dialogOpen}
        onOpenChange={(v) => {
          setDialogOpen(v);
          if (!v) setEditing(null);
        }}
        contacts={contacts}
        companies={companies}
        users={users}
        statuses={statuses}
        stages={stages}
        initial={editing}
        defaultStage={defaultStage}
        onSaved={onSaved}
        onContactCreated={(created) => {
          // Newly-created contact from the nested + Yeni Kişi flow needs
          // to appear in the outer `contacts` list so it renders in future
          // Kontak select rounds.
          setContacts((cur) =>
            cur.find((c) => c.id === created.id) ? cur : [created, ...cur],
          );
        }}
      />

      <DealDetailDrawer
        open={!!detailDeal}
        onOpenChange={(v) => {
          if (!v) {
            setDetailDeal(null);
            setStartInEditMode(false);
          }
        }}
        deal={detailDeal}
        startInEditMode={startInEditMode}
        onEditConsumed={() => setStartInEditMode(false)}
        contacts={contacts}
        companies={companies}
        users={users}
        statuses={statuses}
        stages={stages}
        canManage={canManage}
        onContactCreated={(c) => setContacts((p) => [...p, c])}
        onDealSaved={(saved) => {
          // Merge back into the pipeline list AND refresh the currently-
          // open drawer view so the inline edit result appears without
          // needing a manual re-open.
          setDeals((prev) => prev.map((d) => (d.id === saved.id ? saved : d)));
          setDetailDeal(saved);
          load();
        }}
        onDelete={() => {
          if (detailDeal) {
            remove(detailDeal);
            setDetailDeal(null);
          }
        }}
      />
    </div>
  );
}

// ------- Compact KPI card used by the pipeline dashboard -----------
function DealKpi({ tone, icon, label, value, sub, testId }) {
  const tones = {
    violet: "from-violet-500 to-fuchsia-500",
    fuchsia: "from-fuchsia-500 to-pink-500",
    sky: "from-sky-500 to-cyan-500",
    emerald: "from-emerald-500 to-teal-500",
    amber: "from-amber-500 to-orange-500",
    red: "from-red-500 to-rose-500",
    slate: "from-slate-500 to-zinc-500",
  };
  const gradient = tones[tone] || tones.slate;
  return (
    <div
      className="relative overflow-hidden rounded-xl border border-zinc-200 bg-white px-3 py-2.5 shadow-sm"
      data-testid={testId}
    >
      <div className={`pointer-events-none absolute -top-6 -right-6 h-16 w-16 rounded-full bg-gradient-to-br ${gradient} opacity-10`} />
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
        <span className={`inline-flex h-5 w-5 items-center justify-center rounded-md bg-gradient-to-br ${gradient} text-white`}>
          {icon}
        </span>
        {label}
      </div>
      <div className="mt-1.5 text-xl font-semibold tracking-tight text-zinc-950">
        {value}
      </div>
      {sub && <div className="mt-0.5 truncate text-[10px] text-zinc-500">{sub}</div>}
    </div>
  );
}

// Masked placeholder shown to users without the `can_see_deal_amounts`
// permission. Exported so all deal-related components (kanban card,
// list row, drawer, KPI tiles) render an identical redaction glyph.
export const MASKED_AMOUNT = "••••• ₺";

/** True when the backend flagged this deal's monetary fields as
 * hidden (`amount_masked: true`). Elevated users receive
 * `amount_masked: false`. */
export const isDealMasked = (deal) => !!(deal && deal.amount_masked);

/** Render helper — returns the masked glyph when appropriate, else the
 * caller's own formatted string. */
export function dealAmountDisplay(deal, formatted) {
  return isDealMasked(deal) ? MASKED_AMOUNT : formatted;
}

// ------- Deal Detail Drawer (read view + inline edit) --------------
function DealDetailDrawer({
  open,
  onOpenChange,
  deal,
  contacts,
  companies,
  users,
  statuses,
  stages,
  canManage,
  onDelete,
  onContactCreated,
  onDealSaved,
  startInEditMode = false,
  onEditConsumed,
}) {
  // Local "am I editing right now" flag. Toggled by the Düzenle button
  // *or* set immediately via `startInEditMode` when the parent asks the
  // drawer to jump straight into edit (pencil-icon shortcut on cards).
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    setEditing(false);
  }, [deal?.id]);
  useEffect(() => {
    if (startInEditMode && deal) {
      setEditing(true);
      onEditConsumed?.();
    }
  }, [startInEditMode, deal, onEditConsumed]);

  if (!deal) return null;
  const co = companies.find((c) => c.id === deal.company_id);
  const con = contacts.find((c) => c.id === deal.contact_id);
  const owner = users.find((u) => u.id === deal.owner_user_id);
  const status = statuses.find((s) => s.id === deal.status_id);
  const stage = stages.find((s) => s.key === deal.stage);
  const rawMoney = new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: deal.currency || "TRY",
    maximumFractionDigits: 0,
  }).format(deal.value || 0);
  const fmtMoney = isDealMasked(deal) ? MASKED_AMOUNT : rawMoney;
  const sourceLabels = {
    website: "Web sitesi",
    referral: "Referans",
    political: "Siyasi Bağlantı",
    cold_call: "Soğuk Arama",
    social_media: "Sosyal Medya",
  };
  const priorityLabels = { low: "Düşük", medium: "Orta", high: "Yüksek" };

  const fmtDate = (d) => {
    if (!d) return "—";
    try {
      return new Date(d).toLocaleDateString("tr-TR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    } catch {
      return d;
    }
  };
  const fmtNumber = (n) => {
    if (n === null || n === undefined || n === "") return "—";
    return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 }).format(n);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-lg"
        data-testid="deal-detail-drawer"
      >
        <SheetHeader>
          <div className="flex items-center gap-2">
            {stage && (
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
                style={{
                  background: (stage.color || "#6b7280") + "20",
                  color: stage.color || "#6b7280",
                }}
              >
                <span
                  className="inline-block h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: stage.color || "#6b7280" }}
                />
                {stage.label}
              </span>
            )}
            {deal.probability !== null && deal.probability !== undefined && (
              <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-medium text-violet-700">
                %{deal.probability}
              </span>
            )}
          </div>
          <SheetTitle className="mt-1 text-xl font-semibold tracking-tight text-zinc-950">
            {deal.title}
          </SheetTitle>
          <div className="mt-1 text-2xl font-bold text-zinc-900">{fmtMoney}</div>
        </SheetHeader>

        <div className="mt-5 space-y-4">
          {editing ? (
            /* Inline edit mode — reuses the DealDialog form body so
               create/edit share a single source of truth. Save +
               Cancel buttons live at the bottom of the form itself. */
            <DealDialog
              inline
              open
              onOpenChange={(v) => {
                if (!v) setEditing(false);
              }}
              initial={deal}
              contacts={contacts}
              companies={companies}
              users={users}
              statuses={statuses}
              stages={stages}
              onContactCreated={onContactCreated}
              onSaved={(saved) => {
                setEditing(false);
                onDealSaved?.(saved);
              }}
            />
          ) : (
            <>
          {/* Şirket + Kontak */}
          <div className="rounded-lg border border-zinc-200 bg-white p-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              Müşteri
            </div>
            <div className="mt-1.5 space-y-1.5">
              {co && (
                <div className="flex items-center gap-2 text-sm">
                  <div className="flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-sky-500 to-cyan-500 text-white">
                    <Building2 className="h-3.5 w-3.5" />
                  </div>
                  <span className="font-medium text-zinc-900">{co.name}</span>
                  {status && (
                    <span
                      className="ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
                      style={{
                        background: (status.color || "#6b7280") + "20",
                        color: status.color || "#6b7280",
                      }}
                    >
                      {status.name}
                    </span>
                  )}
                </div>
              )}
              {con && (
                <div className="flex items-start gap-2 text-sm text-zinc-700">
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-[11px] font-semibold text-white">
                    {nameInitials([con.first_name, con.last_name].filter(Boolean).join(" "))}
                  </div>
                  <div className="min-w-0 flex-1">
                    {/* İsim + varsa unvan */}
                    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                      <span className="font-medium text-zinc-900">
                        {[con.first_name, con.last_name].filter(Boolean).join(" ") || "İsimsiz kişi"}
                      </span>
                      {con.title && (
                        <span
                          className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-800"
                          data-testid="drawer-contact-title"
                        >
                          {con.title}
                        </span>
                      )}
                    </div>
                    {/* İletişim satırları — mail + telefon ayrı ayrı,
                        her biri tıklanabilir (mailto:/tel:). Deal'daki
                        email/phone alanları personel bazlı değil, satır
                        bazlı olduğu için önce contact'ın kendi bilgisi,
                        yoksa deal'ın email/phone'una düş. */}
                    {(con.email || deal.email) && (
                      <a
                        href={`mailto:${con.email || deal.email}`}
                        className="mt-0.5 block truncate text-[11px] text-zinc-500 hover:text-violet-700 hover:underline"
                        data-testid="drawer-contact-email"
                      >
                        {con.email || deal.email}
                      </a>
                    )}
                    {(con.phone || deal.phone) && (
                      <a
                        href={`tel:${con.phone || deal.phone}`}
                        className="mt-0.5 block truncate text-[11px] text-zinc-500 hover:text-violet-700 hover:underline"
                        data-testid="drawer-contact-phone"
                      >
                        {con.phone || deal.phone}
                      </a>
                    )}
                    {con.notes && (
                      <div className="mt-1 line-clamp-2 rounded-md bg-zinc-50 px-2 py-1 text-[11px] italic text-zinc-600">
                        {con.notes}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Meta grid — core deal attributes */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <DetailField label="Kaynak" value={sourceLabels[deal.source] || "—"} />
            <DetailField
              label="Sorumlu"
              value={owner ? owner.name : "— Atanmamış —"}
            />
            <DetailField label="Öncelik" value={priorityLabels[deal.priority] || "Orta"} />
            <DetailField
              label="Beklenen Kapanış"
              value={fmtDate(deal.expected_close_date)}
            />
          </div>

          {/* Smartsheet-aligned fields — always render the block so
              users can see at a glance which values are missing.
              Individual fields fall back to "—" for null/empty. */}
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
            <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-900">
              <FileSpreadsheet className="h-3 w-3" />
              Smartsheet Alanları
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <DetailField label="Ülke" value={deal.country || "—"} testId="drawer-country" />
              <DetailField label="Sektör" value={deal.sector || "—"} testId="drawer-sector" />
              <DetailField label="E-posta" value={deal.email || "—"} testId="drawer-email" />
              <DetailField label="Telefon" value={deal.phone || "—"} testId="drawer-phone" />
              <DetailField
                label="Katılım Tipi"
                value={deal.participation_type || "—"}
                testId="drawer-participation-type"
              />
              <DetailField
                label="Katılım Ücreti"
                value={
                  deal.participation_cost !== null && deal.participation_cost !== undefined
                    ? `$${fmtNumber(deal.participation_cost)}`
                    : "—"
                }
                testId="drawer-participation-cost"
              />
              <DetailField
                label="Metrekare"
                value={
                  deal.metersquare !== null && deal.metersquare !== undefined
                    ? `${fmtNumber(deal.metersquare)} m²`
                    : "—"
                }
                testId="drawer-metersquare"
              />
              <DetailField
                label="Son Temas"
                value={fmtDate(deal.last_contacted)}
                testId="drawer-last-contacted"
              />
            </div>
          </div>

          {/* Probability + created/updated meta */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <DetailField
              label="Olasılık"
              value={
                deal.probability !== null && deal.probability !== undefined
                  ? `%${deal.probability}`
                  : "—"
              }
            />
            <DetailField
              label="Değer"
              value={fmtMoney}
            />
          </div>

          {/* Smartsheet sync stamp — small line, only when synced */}
          {deal.smartsheet_row_id && (
            <div className="rounded-md bg-emerald-50 px-2 py-1.5 text-[10px] text-emerald-800 ring-1 ring-emerald-200">
              Smartsheet satır #{deal.smartsheet_row_id} · Son sync:{" "}
              {deal.smartsheet_last_synced_at
                ? new Date(deal.smartsheet_last_synced_at).toLocaleString("tr-TR", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "—"}
            </div>
          )}

          {/* Tags */}
          {deal.tags && deal.tags.length > 0 && (
            <div>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                Etiketler
              </div>
              <div className="flex flex-wrap gap-1.5">
                {deal.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-violet-100 px-2.5 py-0.5 text-[11px] font-medium text-violet-800"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {deal.notes && (
            <div>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                Notlar
              </div>
              <div className="whitespace-pre-wrap rounded-md border border-zinc-200 bg-white p-3 text-sm text-zinc-700">
                {deal.notes}
              </div>
            </div>
          )}
            </>
          )}
        </div>

        {/* Sticky footer actions — hidden in edit mode (the inline form
            renders its own Save/Cancel row). */}
        {!editing && (
        <div className="sticky bottom-0 mt-6 flex items-center justify-between gap-2 border-t border-zinc-200 bg-white pt-3">
          {canManage ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="gap-1 text-red-600 hover:bg-red-50 hover:text-red-700"
              data-testid="drawer-deal-delete"
            >
              <Trash2 className="h-4 w-4" />
              Sil
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}>
              Kapat
            </Button>
            {canManage && (
              <Button
                size="sm"
                onClick={() => setEditing(true)}
                className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
                data-testid="drawer-deal-edit"
              >
                <Pencil className="h-4 w-4" />
                Düzenle
              </Button>
            )}
          </div>
        </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function DetailField({ label, value, testId }) {
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-2" data-testid={testId}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
        {label}
      </div>
      <div className="mt-0.5 truncate text-sm text-zinc-900">{value}</div>
    </div>
  );
}
